/**
 * Equal-width binning utility and Pearson correlation for the Correlogram.
 */

export interface HistogramBin {
  x0: number;
  x1: number;
  mid: number;
  count: number;
}

export function binData(values: number[], binCount?: number): HistogramBin[] {
  const clean = values.filter(v => Number.isFinite(v));
  if (clean.length === 0) return [];

  let min = clean[0]!;
  let max = clean[0]!;
  for (let i = 1; i < clean.length; i++) {
    const v = clean[i]!;
    if (v < min) min = v;
    if (v > max) max = v;
  }

  if (min === max) {
    return [{ x0: min - 0.5, x1: max + 0.5, mid: min, count: clean.length }];
  }

  const nBins = binCount ?? Math.max(8, Math.min(20, Math.ceil(Math.log2(clean.length) + 1)));
  const width = (max - min) / nBins;

  const bins: HistogramBin[] = [];
  for (let i = 0; i < nBins; i++) {
    const x0 = min + i * width;
    const x1 = i === nBins - 1 ? max : min + (i + 1) * width;
    bins.push({ x0, x1, mid: (x0 + x1) / 2, count: 0 });
  }

  for (const v of clean) {
    let idx = Math.floor((v - min) / width);
    if (idx >= nBins) idx = nBins - 1;
    bins[idx]!.count++;
  }

  return bins;
}

// ---------------------------------------------------------------------------
// KDE (kernel density estimation) for violin plots
// ---------------------------------------------------------------------------

export interface KdePoint {
  value: number;
  density: number;
}

/**
 * Gaussian KDE. Returns an array of (value, density) points suitable for
 * plotting a violin / density curve.
 */
export function gaussianKde(values: number[], nPoints = 64): KdePoint[] {
  const clean = values.filter(v => Number.isFinite(v));
  if (clean.length === 0) return [];

  clean.sort((a, b) => a - b);
  const min = clean[0]!;
  const max = clean[clean.length - 1]!;
  const span = max - min || 1;

  // Silverman's rule of thumb for bandwidth
  const n = clean.length;
  const mean = clean.reduce((a, b) => a + b, 0) / n;
  const variance = clean.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance) || 1;
  const h = 1.06 * std * Math.pow(n, -0.2);

  const pad = span * 0.1;
  const lo = min - pad;
  const hi = max + pad;
  const step = (hi - lo) / (nPoints - 1);

  const result: KdePoint[] = [];
  for (let i = 0; i < nPoints; i++) {
    const x = lo + i * step;
    let sum = 0;
    for (const v of clean) {
      const u = (x - v) / h;
      sum += Math.exp(-0.5 * u * u);
    }
    result.push({ value: x, density: sum / (n * h * Math.sqrt(2 * Math.PI)) });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Box plot statistics
// ---------------------------------------------------------------------------

export interface BoxPlotStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers: number[];
  iqr: number;
  whiskerLow: number;
  whiskerHigh: number;
}

export function computeBoxPlotStats(values: number[]): BoxPlotStats | null {
  const clean = values.filter(v => Number.isFinite(v));
  if (clean.length === 0) return null;
  clean.sort((a, b) => a - b);

  const n = clean.length;
  const q1 = clean[Math.floor(n * 0.25)]!;
  const median = clean[Math.floor(n * 0.5)]!;
  const q3 = clean[Math.floor(n * 0.75)]!;
  const iqr = q3 - q1;
  const whiskerLow = Math.max(clean[0]!, q1 - 1.5 * iqr);
  const whiskerHigh = Math.min(clean[n - 1]!, q3 + 1.5 * iqr);
  const outliers = clean.filter(v => v < whiskerLow || v > whiskerHigh);

  return {
    min: clean[0]!,
    q1,
    median,
    q3,
    max: clean[n - 1]!,
    outliers,
    iqr,
    whiskerLow,
    whiskerHigh,
  };
}

// ---------------------------------------------------------------------------
// 2D hexagonal binning for density heatmaps
// ---------------------------------------------------------------------------

export interface HexBin {
  cx: number;
  cy: number;
  count: number;
}

export function hexBin2d(
  data: { x: number; y: number }[],
  xRange: [number, number],
  yRange: [number, number],
  gridSize = 12,
): HexBin[] {
  if (data.length === 0) return [];

  const xSpan = xRange[1] - xRange[0] || 1;
  const ySpan = yRange[1] - yRange[0] || 1;
  const hexW = xSpan / gridSize;
  const hexH = ySpan / gridSize;

  const bins = new Map<string, { cx: number; cy: number; count: number }>();

  for (const pt of data) {
    // Offset hex grid
    const col = Math.floor((pt.x - xRange[0]) / hexW);
    const row = Math.floor((pt.y - yRange[0]) / hexH);
    const key = `${col},${row}`;
    if (!bins.has(key)) {
      bins.set(key, {
        cx: xRange[0] + (col + 0.5) * hexW,
        cy: yRange[0] + (row + 0.5) * hexH,
        count: 0,
      });
    }
    bins.get(key)!.count++;
  }

  return [...bins.values()];
}

// ---------------------------------------------------------------------------
// LTTB (Largest Triangle Three Buckets) downsampling
// ---------------------------------------------------------------------------

/**
 * Downsample a 2D dataset to `targetSize` points while preserving visual shape.
 * Uses the Largest Triangle Three Buckets algorithm.
 *
 * @param data  Array of {x, y, idx} points (need not be sorted).
 * @param targetSize  Desired number of output points (including first/last).
 * @returns  A subset of `data` of length min(data.length, targetSize).
 */
export function lttbDownsample<T extends { x: number; y: number }>(
  data: T[],
  targetSize: number,
): T[] {
  const n = data.length;
  if (n <= targetSize) return data;
  if (targetSize <= 0) return [];
  if (targetSize === 1) return [data[0]!];
  if (targetSize === 2) {
    const sorted = [...data].sort((a, b) => a.x - b.x || a.y - b.y);
    return [sorted[0]!, sorted[n - 1]!];
  }

  // Sort by x
  const sorted = [...data].sort((a, b) => a.x - b.x || a.y - b.y);

  const sampled: T[] = [sorted[0]!]; // Always keep first
  const bucketSize = (n - 2) / (targetSize - 2);

  let prevSelected = 0; // index into sorted[]

  for (let bucket = 0; bucket < targetSize - 2; bucket++) {
    // Current bucket range
    const bucketStart = Math.floor(bucket * bucketSize) + 1;
    const bucketEnd = Math.min(Math.floor((bucket + 1) * bucketSize) + 1, n - 1);

    // Next bucket average (for triangle area calculation)
    const nextBucketStart = Math.min(Math.floor((bucket + 1) * bucketSize) + 1, n - 1);
    const nextBucketEnd = Math.min(Math.floor((bucket + 2) * bucketSize) + 1, n);
    let avgX = 0, avgY = 0;
    const nextCount = nextBucketEnd - nextBucketStart;
    if (nextCount > 0) {
      for (let k = nextBucketStart; k < nextBucketEnd; k++) {
        avgX += sorted[k]!.x;
        avgY += sorted[k]!.y;
      }
      avgX /= nextCount;
      avgY /= nextCount;
    } else {
      // Last bucket: use the last point
      avgX = sorted[n - 1]!.x;
      avgY = sorted[n - 1]!.y;
    }

    // Find the point in this bucket that maximizes triangle area
    const prev = sorted[prevSelected]!;
    let maxArea = -1;
    let bestIdx = bucketStart;

    for (let k = bucketStart; k < bucketEnd; k++) {
      const pt = sorted[k]!;
      // Triangle area = 0.5 * |x_a(y_b - y_c) + x_b(y_c - y_a) + x_c(y_a - y_b)|
      const area = Math.abs(
        (prev.x - avgX) * (pt.y - prev.y) -
        (prev.x - pt.x) * (avgY - prev.y),
      );
      if (area > maxArea) {
        maxArea = area;
        bestIdx = k;
      }
    }

    sampled.push(sorted[bestIdx]!);
    prevSelected = bestIdx;
  }

  sampled.push(sorted[n - 1]!); // Always keep last
  return sampled;
}

// ---------------------------------------------------------------------------
// Pearson correlation
// ---------------------------------------------------------------------------

export function pearsonCorrelation(x: number[], y: number[]): number {
  const n = Math.min(x.length, y.length);
  if (n < 2) return NaN;

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  let validN = 0;

  for (let i = 0; i < n; i++) {
    const xi = x[i]!, yi = y[i]!;
    if (!Number.isFinite(xi) || !Number.isFinite(yi)) continue;
    sumX += xi;
    sumY += yi;
    sumXY += xi * yi;
    sumX2 += xi * xi;
    sumY2 += yi * yi;
    validN++;
  }

  if (validN < 2) return NaN;

  const denom = Math.sqrt(
    (validN * sumX2 - sumX * sumX) * (validN * sumY2 - sumY * sumY),
  );
  if (denom === 0) return 0;
  return (validN * sumXY - sumX * sumY) / denom;
}

/**
 * Partial correlation of x and y controlling for z.
 * r_xy.z = (r_xy - r_xz * r_yz) / sqrt((1 - r_xz²)(1 - r_yz²))
 * Answers: "are x and y still coupled after removing z's influence?"
 */
export function partialCorrelation(x: number[], y: number[], z: number[]): number {
  const n = Math.min(x.length, y.length, z.length);
  if (n < 3) return NaN;

  // Filter to rows where all three are finite
  const xf: number[] = [], yf: number[] = [], zf: number[] = [];
  for (let i = 0; i < n; i++) {
    if (Number.isFinite(x[i]!) && Number.isFinite(y[i]!) && Number.isFinite(z[i]!)) {
      xf.push(x[i]!); yf.push(y[i]!); zf.push(z[i]!);
    }
  }
  if (xf.length < 3) return NaN;

  const rxy = pearsonCorrelation(xf, yf);
  const rxz = pearsonCorrelation(xf, zf);
  const ryz = pearsonCorrelation(yf, zf);
  if (!Number.isFinite(rxy) || !Number.isFinite(rxz) || !Number.isFinite(ryz)) return NaN;

  const denom = Math.sqrt((1 - rxz * rxz) * (1 - ryz * ryz));
  if (denom === 0) return 0;
  return (rxy - rxz * ryz) / denom;
}

/**
 * Multiple correlation coefficient R for y predicted by x and z.
 * R² = (r_xy² + r_yz² - 2*r_xy*r_yz*r_xz) / (1 - r_xz²)
 * Answers: "how well do two axes together explain the third?"
 */
export function multipleCorrelationR(x: number[], y: number[], z: number[]): number {
  const n = Math.min(x.length, y.length, z.length);
  if (n < 3) return NaN;

  const xf: number[] = [], yf: number[] = [], zf: number[] = [];
  for (let i = 0; i < n; i++) {
    if (Number.isFinite(x[i]!) && Number.isFinite(y[i]!) && Number.isFinite(z[i]!)) {
      xf.push(x[i]!); yf.push(y[i]!); zf.push(z[i]!);
    }
  }
  if (xf.length < 3) return NaN;

  const rxy = pearsonCorrelation(xf, yf);
  const rxz = pearsonCorrelation(xf, zf);
  const ryz = pearsonCorrelation(yf, zf);
  if (!Number.isFinite(rxy) || !Number.isFinite(rxz) || !Number.isFinite(ryz)) return NaN;

  const rxz2 = rxz * rxz;
  if (rxz2 >= 1) return NaN;
  const R2 = (rxy * rxy + ryz * ryz - 2 * rxy * ryz * rxz) / (1 - rxz2);
  if (R2 < 0) return 0;
  return Math.sqrt(Math.min(R2, 1));
}
